import type { BrandLocaleTextInput } from "@cfg/brand-sdk";
import { attributionEs, privacyEs, termsEs } from "../legal.ts";

/**
 * BrickBrackets text in Spanish (Mexico): the brand's own wording on top of
 * the core Spanish catalog (packages/brand-sdk/src/locales/es-MX.ts).
 *
 * Translators: fill in the empty strings. The comment above each entry is the
 * English this replaces (from ../brand.config.ts). Keep `{placeholders}` (e.g.
 * `{ref}`) exactly as written, and keep "LEGO®" as is. An empty string means
 * "not translated yet": the core Spanish text is used, then the English.
 * Serializable: this object becomes part of brand.json at deploy.
 */
export const esMX: BrandLocaleTextInput = {
  // Parametric display brackets for LEGO® sets.
  tagline: "Soportes paramétricos para exhibir tus sets de LEGO®.",
  fitTags: {
    // LEGO® set
    label: "set de LEGO®",
    // e.g. 42115-1   (keep the example number)
    refPlaceholder: "p. ej. 42115-1",
    // Set name (optional)
    namePlaceholder: "Nombre del set (opcional)",
    // LEGO® is a trademark of the LEGO Group, which does not sponsor, authorize or endorse this site.
    disclaimer: "LEGO® es una marca comercial del Grupo LEGO, el cual no patrocina, autoriza ni respalda este sitio.",
  },
  // Privacy, terms and the Rebrickable credit live in ../legal.ts (privacyEs, termsEs, attributionEs).
  legal: { privacy: privacyEs, terms: termsEs, attribution: attributionEs },
  meta: {
    // Parametric display brackets for LEGO® sets. Set the dimensions, check the print, download the STL. Generated in your browser.
    // (Used for the page's meta description after the language switches; search/social previews keep the English.)
    description: "Soportes paramétricos para exhibir tus sets de LEGO®. Ajusta las medidas, revisa la impresión y descarga el STL. Generados en tu navegador.",
  },
  copy: {
    // Brackets
    "nav.catalog": "Soportes",
    // My brackets
    "nav.mine": "Mis soportes",
    // Pick a bracket
    "catalog.title": "Elige un soporte",
    // No brackets published yet.
    "catalog.empty": "Aún no hay soportes publicados.",
    // Dimensions
    "configurator.params": "Medidas",
    // Print check
    "configurator.warnings": "Revisión de impresión",
    // My brackets
    "mine.title": "Mis soportes",
    // Nothing saved yet. Configure a bracket and press Save.
    "mine.empty": "Aún no has guardado nada. Configura un soporte y presiona Guardar.",
    // Community brackets
    "community.title": "Soportes de la comunidad",
    // Set number, name or person
    "community.searchLabel": "Número o nombre del set, o persona",
    // Tag the LEGO® set a bracket fits from the Dimensions tab to list it here.
    "community.emptyHint": "Etiqueta el set de LEGO® al que le queda un soporte desde la pestaña Medidas para que aparezca aquí.",
    // Mount
    "family.label": "Montaje",
    // No favorites yet. Use ☆ Favorite on a community bracket.
    "mine.favoritesEmpty": "Aún no tienes favoritos. Usa ☆ Favorito en un soporte de la comunidad.",
    // Added to Favorites in My brackets.
    "community.favorited": "Agregado a Favoritos en Mis soportes.",
    // Add set
    "community.addFitSubmit": "Agregar set",
    // Back to my dimensions
    "community.restore": "Volver a mis medidas",
    // A community bracket is on the model, so the dimensions are locked.
    "community.locked": "Hay un soporte de la comunidad en el modelo; las medidas están bloqueadas.",
    // LEGO® sets it fits
    "community.setsLabel": "Sets de LEGO® a los que le queda",
    // Community bracket on the model
    "community.onModelLabel": "Soporte de la comunidad en el modelo",
    // {count} sets
    "community.sets.other": "{count} sets",
    // 1 set
    "community.sets.one": "1 set",
    // Saved to My brackets.
    "configurator.savedProfile": "Guardado en Mis soportes.",
    // My bracket
    "configurator.saveHeading": "Mi soporte",
    // No sets yet. Tag the set a bracket fits from its Dimensions tab.
    "mine.taggedEmpty": "Aún no tienes sets. Etiqueta el set al que le queda un soporte desde su pestaña Medidas.",
    // Your sets
    "mine.tagged": "Tus sets",
    // These dimensions are already tagged for {refs}.
    "community.alreadyTagged": "Estas medidas ya están etiquetadas para {refs}.",
    // Tell other fans which set these dimensions fit. Your name shows next to the set; you can remove it any time.
    "community.tagHint": "Cuéntale a otros fans a qué set le quedan estas medidas. Tu nombre aparece junto al set; puedes quitarlo cuando quieras.",
    // Tag a {label} it fits…
    "community.tag": "Etiquetar el set de LEGO® al que le queda…",
    // New bracket
    "configurator.new": "Nuevo soporte",
    // Start a new bracket from these dimensions. The saved one stays as it is.
    "configurator.newHint": "Empieza un soporte nuevo con estas medidas. El guardado se queda como está.",
    // Started a new bracket from these dimensions.
    "configurator.startedNew": "Empezaste un soporte nuevo con estas medidas.",
    // Add a LEGO® set it fits
    "community.addFit": "Agregar un set de LEGO® al que le queda",
    // I printed it for this set
    "community.addFitPrinted": "Lo imprimí para este set",
    // Type a set number or name and pick the set, or type its number (42115 or 42115-1). Set data from Rebrickable.
    "fits.hint": "Escribe el número o el nombre del set y elígelo, o escribe su número (42115 o 42115-1). Datos de sets de Rebrickable.",
    // Ask for a new link, or start from a bracket.
    "share.missingBody": "Pide un enlace nuevo o empieza desde un soporte.",
    // Browse brackets
    "share.browse": "Ver soportes",
    // I've printed this and it fits my set
    "fits.printed": "Ya lo imprimí y le queda bien a mi set",
    // Find brackets for your LEGO® set
    "home.findLabel": "Encuentra soportes para tu set de LEGO®",
    // Set number or name
    "home.findPlaceholder": "Número o nombre del set",
    // Brackets tagged for {ref}
    "home.resultsTitle": "Soportes etiquetados para {ref}",
    // 1 bracket tagged for this set
    "home.entries.one": "1 soporte etiquetado para este set",
    // {count} brackets tagged for this set
    "home.entries.other": "{count} soportes etiquetados para este set",
    // Pick a bracket below, size it to your set and tag it with {ref} from the Dimensions tab, so the next fan finds it here.
    "home.noneBody": "Elige un soporte de abajo, ajústalo a tu set y etiquétalo con {ref} desde la pestaña Medidas, para que el próximo fan lo encuentre aquí.",
    // All brackets
    "home.allTitle": "Todos los soportes",
    // Find your set
    "home.step1": "Encuentra tu set",
    // Pick a bracket people printed
    "home.step2": "Elige un soporte que otros imprimieron",
    // {count} sets already have brackets tagged by the community
    "home.proof": "{count} sets ya tienen soportes etiquetados por la comunidad",
    // Community bracket on the model.
    "community.appliedLive": "Soporte de la comunidad en el modelo.",
    // {ref} is this bracket's only set: removing your tag removes the bracket from the community, prints included.
    "community.removeLast": "{ref} es el único set de este soporte: al quitar tu etiqueta, el soporte sale de la comunidad, con sus impresiones.",
    // Start from this bracket
    "community.adopt": "Empezar desde este soporte",
    // Could not open this bracket
    "configurator.openError": "No se pudo abrir este soporte",
    // Bracket not found
    "configurator.notFound": "Soporte no encontrado",
    // No tagged set matches “{query}”. Type the set number, like 10294, or size a bracket to your set below and be the first to tag it.
    "home.noMatch": "Ningún set etiquetado coincide con “{query}”. Escribe el número del set, como 10294, o ajusta un soporte de abajo a tu set y sé el primero en etiquetarlo.",
    // Customize your own ↓
    "home.noMatchCustomize": "Personaliza el tuyo ↓",
    // We couldn't find that set. Try its number, like 42115, or customize your own bracket below.
    "home.invalid": "No encontramos ese set. Prueba con su número, como 42115, o personaliza tu propio soporte abajo.",
    // That doesn't look like a set number. Try one like 42115.
    "fits.invalid": "Eso no parece un número de set. Prueba con uno como 42115.",
    // Only brackets tagged for
    "community.fitFilter": "Solo soportes etiquetados para",
    // I printed it: tag my LEGO® set
    "next.printedTag": "Lo imprimí: etiquetar mi set de LEGO®",
    // Which set did you print it for?
    "next.printedPick": "¿Para qué set lo imprimiste?",
    // Save it to My brackets, to tweak it after a test print
    "next.save": "Guárdalo en Mis soportes para ajustarlo después de una impresión de prueba",
    // No bracket of this mount is tagged for {ref} yet.
    "community.fitNone": "Ningún soporte de este montaje está etiquetado para {ref} todavía.",
  },
};
